import { Box, Stack, Typography } from "@mui/material"
import parse, { domToReact, type HTMLReactParserOptions } from "html-react-parser"

import { Grid, SvgIcon, type FC } from ".."

type ColumnProps = {
  title: string

  items: string
}

type SplitListType = "default" | "pros-and-cons"

export type SplitListProps = {
  listStyle: SplitListType
  leftContent: ColumnProps
  rightContent: ColumnProps
}

const SplitList: FC<SplitListProps> = ({ listStyle = "default", leftContent, rightContent }) => {
  return (
    <Grid component="article" sx={{ rowGap: 10 }}>
      <ColumnContent position="left" listStyle={listStyle} content={leftContent} />
      <ColumnContent position="right" listStyle={listStyle} content={rightContent} />
    </Grid>
  )
}

export default SplitList

/** A sub-component for the column content */
type ContentProps = {
  position: "left" | "right"
  listStyle: SplitListType
  content: ColumnProps
}

const ColumnContent: FC<ContentProps> = ({ listStyle, position, content }) => {
  const parseOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if ("name" in domNode && "attribs" in domNode) {
        const { name, children } = domNode
        if (name === "ul" || name === "ol") {
          return (
            <Stack rowGap={position === "left" ? { xs: 7, md: 10 } : 5}>
              {domToReact(children, parseOptions)}
            </Stack>
          )
        }
        if (name === "li") {
          return (
            <Stack direction="row" columnGap={5}>
              <SvgIcon
                variant={
                  listStyle === "default"
                    ? "bullet"
                    : position === "left"
                    ? "checkmarkList"
                    : "cross"
                }
                size={10}
                sx={{
                  fill: (theme) => theme.palette["primary.800"].main,
                  mt: position === "left" ? 2.5 : 1.75,
                }}
              />
              <Typography
                component="div"
                variant={position === "left" ? "body.lg" : "body.md"}
                lineHeight={{ xs: "2", md: "1.5" }}
                sx={{ "& > p": { margin: 0 } }}
              >
                {domToReact(children, parseOptions)}
              </Typography>
            </Stack>
          )
        }
      }
    },
  }
  return (
    <Box
      gridColumn={
        position === "left"
          ? { xs: "1 / -1", md: "1 / span 5", lg: "2 / span 6" }
          : { xs: "1 / -1", md: "6 / span 3", lg: "8 / span 4" }
      }
      borderLeft={(theme) =>
        position === "right"
          ? { xs: "0", md: `1px solid ${theme.palette["neutral.200"].main}` }
          : "0"
      }
      borderTop={(theme) =>
        position === "right"
          ? { xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "0" }
          : "0"
      }
      pt={{
        xs: position === "right" ? 10 : 0,
        md: 0,
      }}
      pl={position === "right" ? { md: 15, lg: 20, xl: 30 } : 0}
      pr={position === "left" ? { md: 5, lg: 0 } : 0}
    >
      <Typography variant="heading.lg" component="div" mb={10}>
        {content?.title}
      </Typography>
      {content?.items && parse(content.items, parseOptions)}
    </Box>
  )
}
